import { useCallback, useContext, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router";
import { Button } from "primereact/button";
import { useBreadcrumb } from "sj-ui-toolkit/src/atlas/hooks/useBreadcrumbs";
import { AdminAppRoutes } from "sj-ui-toolkit/src/constants/routes";
import { getCurrentSelectedTenant } from "sj-ui-toolkit/src/utils";
import { getChoresClient } from "sj-ui-toolkit/src/client";
import { useToast } from "sj-ui-toolkit/src/hooks";
import { AppContext } from "@/context/AppContext";
import { loadAllAssessments } from "@/api/assessments";
import type { AssessmentData } from "@/api/assessments";
import KnowledgeAssessmentsTable from "@/components/assessments/KnowledgeAssessmentsTable";
import type { StatusOption } from "@/components/assessments/KnowledgeAssessmentsTable";

// ---------------------------------------------------------------------------
// Page
// ---------------------------------------------------------------------------

const KnowledgeAssessments = () => {
  useBreadcrumb({
    label: "Assessments",
    route: AdminAppRoutes.routes.assessments.route,
    display: "none",
    resetTrail: true,
  });

  const navigate = useNavigate();
  const { showToast } = useToast();
  const { state } = useContext(AppContext);
  const { viewOnly } = state;

  const tenantName = getCurrentSelectedTenant() ?? "";

  const [assessments, setAssessments] = useState<AssessmentData[] | "loading">("loading");
  const [statusList, setStatusList] = useState<StatusOption[]>([]);

  const hasLoadedRef = useRef(false);

  const loadData = useCallback(async () => {
    try {
      const [assessmentData, statusData] = await Promise.all([
        loadAllAssessments(tenantName),
        getChoresClient()
          .getAvailableStatuses()
          .then(({ data }) => data?.items ?? []),
      ]);
      setAssessments(assessmentData);
      setStatusList(statusData);
    } catch (error) {
      console.error("Failed to load assessments:", error);
      showToast({ severity: "error", detail: "Failed to load assessments" });
      setAssessments([]);
    }
  }, [tenantName, showToast]);

  useEffect(() => {
    if (hasLoadedRef.current) return;
    hasLoadedRef.current = true;
    void loadData();
  }, []); // empty deps — intentional: one-time load on mount (hasLoadedRef guard)

  const refreshAssessments = useCallback(async () => {
    setAssessments("loading");
    await loadData();
  }, [loadData]);

  return (
    <>
      <div className="mb-4 flex items-center justify-between">
        <h1>Knowledge Assessments</h1>
        <Button
          type="button"
          label="Create New Assessment"
          icon="pi pi-plus"
          disabled={viewOnly}
          data-testid="create-assessment-button"
          onClick={() => navigate(AdminAppRoutes.routes.assessments.routes.new.route)}
        />
      </div>
      <div data-testid="knowledge-assessments-table">
        <KnowledgeAssessmentsTable
          assessments={assessments}
          statusList={statusList}
          onArchiveStateChange={refreshAssessments}
          viewOnly={viewOnly}
        />
      </div>
    </>
  );
};

export default KnowledgeAssessments;
